import { Autocomplete, TextField } from '@mui/material';
import { createCustomerPageSelectors, getSelectorAttribute } from '@vy/app-selectors';
import type { NewCustomer } from '@vy/types';
import { type Nullable } from '@vy/utils';
import { countries } from 'countries-list';
import React, { useState, useMemo, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import type { Optional } from '@vy/utils';

type CountriesDropDownProps = {
  defaultSelectedCountry?: Optional<string>;
};

export const CountriesDropDown: React.FC<CountriesDropDownProps> = ({ defaultSelectedCountry }) => {
  const [query, setQuery] = useState(defaultSelectedCountry ?? '');
  const {
    getFieldState,
    setValue,
    formState: { errors },
  } = useFormContext<NewCustomer>();

  const hasError = getFieldState('address.country')?.invalid;

  const countryOptions = useMemo(() => Object.values(countries).map((country) => country.name), []);

  const filteredCountries = useMemo(() => {
    if (!query) return countryOptions;

    return countryOptions.filter((name) => name.toLowerCase().includes(query.toLowerCase()));
  }, [query, countryOptions]);

  const handleCountrySelect = useCallback(
    (event: React.SyntheticEvent<Element, Event>, selectedCountry: Nullable<string>) => {
      setQuery(selectedCountry ?? '');

      const isValid = countryOptions.includes(selectedCountry ?? '');
      setValue('address.country', isValid ? (selectedCountry ?? undefined) : undefined, {
        shouldDirty: true,
      });
    },
    [setValue, countryOptions],
  );

  return (
    <Autocomplete
      value={countryOptions.includes(query) ? query : null}
      onChange={handleCountrySelect}
      options={filteredCountries}
      isOptionEqualToValue={(option, value) => option === value}
      renderInput={(params) => (
        <TextField
          {...getSelectorAttribute(createCustomerPageSelectors.companyDetails.country)}
          {...params}
          label="Country / region"
          placeholder="Country / region"
          error={hasError}
          helperText={errors?.address?.country?.message || ''}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}
    />
  );
};
